import { useState, useCallback } from 'react';
import { Plus, Loader2, Truck, Trash2, Edit, CheckCircle } from 'lucide-react';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Modal, ConfirmDialog, InputDecimal } from '../ui';
import { useApiEffect } from '../../hooks';
import type { DespachanteVeiculo, DespachanteVeiculoCreate, DespachanteVeiculoResumo, TipoServicoDespachante, StatusDespachante, DocumentoDespachante } from '../../types';
import { formatCurrency, formatDate } from '../../utils/format';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  veiculoId: number;
  veiculoNome: string;
}

const TIPOS_SERVICO: { value: TipoServicoDespachante; label: string }[] = [
  { value: 'TRANSFERENCIA', label: 'Transferência' },
  { value: 'PRIMEIRO_EMPLACAMENTO', label: 'Primeiro Emplacamento' },
  { value: 'BAIXA', label: 'Baixa' },
  { value: 'LICENCIAMENTO', label: 'Licenciamento' },
  { value: 'OUTROS', label: 'Outros' },
];

const statusColors: Record<StatusDespachante, string> = {
  PENDENTE: 'bg-yellow-100 text-yellow-700',
  EM_ANDAMENTO: 'bg-blue-100 text-blue-700',
  CONCLUIDO: 'bg-green-100 text-green-700',
  CANCELADO: 'bg-gray-100 text-gray-500',
};

const statusLabels: Record<StatusDespachante, string> = {
  PENDENTE: 'Pendente',
  EM_ANDAMENTO: 'Em Andamento',
  CONCLUIDO: 'Concluído',
  CANCELADO: 'Cancelado',
};

const DOCUMENTOS_PADRAO = [
  'CRV/CRLV',
  'DUT Preenchido',
  'Comprovante de Residência',
  'Documento do Comprador',
  'Documento do Vendedor',
  'Procuração',
];

const emptyForm: DespachanteVeiculoCreate = {
  tipoServico: 'TRANSFERENCIA',
  despachante: '',
  valorServico: undefined,
  valorTaxas: undefined,
  dataSolicitacao: new Date().toISOString().split('T')[0],
  dataPrevistaEntrega: '',
  protocolo: '',
  documentos: [],
  observacoes: '',
};

export function DespachanteVeiculoModal({ isOpen, onClose, veiculoId, veiculoNome }: Props) {
  const { showSuccess, showError, showApiError } = useNotification();

  const [servicos, setServicos] = useState<DespachanteVeiculo[]>([]);
  const [resumo, setResumo] = useState<DespachanteVeiculoResumo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<DespachanteVeiculoCreate>(emptyForm);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const loadData = useCallback(async (signal: AbortSignal) => {
    if (!veiculoId) return;
    setIsLoading(true);

    try {
      const [servicosRes, resumoRes] = await Promise.all([
        api.get<DespachanteVeiculo[]>(`/veiculos/${veiculoId}/despachante`, { signal }),
        api.get<DespachanteVeiculoResumo>(`/veiculos/${veiculoId}/despachante/resumo`, { signal }),
      ]);
      setServicos(servicosRes.data);
      setResumo(resumoRes.data);
    } finally {
      setIsLoading(false);
    }
  }, [veiculoId]);

  const { refetch } = useApiEffect(loadData, [veiculoId], { enabled: isOpen && !!veiculoId });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.dataSolicitacao) {
      showError('Data de solicitação é obrigatória');
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingId) {
        await api.put(`/veiculos/${veiculoId}/despachante/${editingId}`, form);
        showSuccess('Serviço atualizado');
      } else {
        await api.post(`/veiculos/${veiculoId}/despachante`, form);
        showSuccess('Serviço cadastrado');
      }
      setShowForm(false);
      setEditingId(null);
      setForm(emptyForm);
      refetch();
    } catch (error) {
      showApiError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (servico: DespachanteVeiculo) => {
    setEditingId(servico.id);
    setForm({
      tipoServico: servico.tipoServico,
      despachante: servico.despachante || '',
      valorServico: servico.valorServico,
      valorTaxas: servico.valorTaxas,
      dataSolicitacao: servico.dataSolicitacao.split('T')[0],
      dataPrevistaEntrega: servico.dataPrevistaEntrega?.split('T')[0] || '',
      protocolo: servico.protocolo || '',
      documentos: servico.documentos || [],
      observacoes: servico.observacoes || '',
    });
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await api.delete(`/veiculos/${veiculoId}/despachante/${deletingId}`);
      showSuccess('Serviço excluído');
      setShowDeleteDialog(false);
      setDeletingId(null);
      refetch();
    } catch (error) {
      showApiError(error);
    }
  };

  const toggleDocumento = (nome: string) => {
    const docs = form.documentos || [];
    const existing = docs.find((d) => d.nome === nome);
    if (existing) {
      setForm({ ...form, documentos: docs.filter((d) => d.nome !== nome) });
    } else {
      const newDoc: DocumentoDespachante = { nome, recebido: false };
      setForm({ ...form, documentos: [...docs, newDoc] });
    }
  };

  const toggleDocumentoRecebido = (nome: string) => {
    const docs = form.documentos || [];
    setForm({
      ...form,
      documentos: docs.map((d) =>
        d.nome === nome ? { ...d, recebido: !d.recebido, dataRecebimento: !d.recebido ? new Date().toISOString() : undefined } : d
      ),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Despachante - ${veiculoNome}`} size="xl">
      <div className="space-y-4">
        {/* Resumo */}
        {resumo && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 bg-gray-50 rounded-lg">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-lg font-semibold">{resumo.totalServicos}</p>
            </div>
            <div>
              <p className="text-sm text-blue-600">Em Andamento</p>
              <p className="text-lg font-semibold text-blue-600">{resumo.emAndamento}</p>
            </div>
            <div>
              <p className="text-sm text-green-600">Concluídos</p>
              <p className="text-lg font-semibold text-green-600">{resumo.concluidos}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Valor Total</p>
              <p className="text-lg font-semibold">{formatCurrency(resumo.valorTotal)}</p>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="flex justify-end">
          <button
            onClick={() => {
              setShowForm(true);
              setEditingId(null);
              setForm(emptyForm);
            }}
            className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
          >
            <Plus className="w-4 h-4" />
            Novo Serviço
          </button>
        </div>

        {/* Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo de Serviço</label>
                <select
                  value={form.tipoServico}
                  onChange={(e) => setForm({ ...form, tipoServico: e.target.value as TipoServicoDespachante })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {TIPOS_SERVICO.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Despachante</label>
                <input
                  type="text"
                  value={form.despachante}
                  onChange={(e) => setForm({ ...form, despachante: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Nome do despachante"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor do Serviço</label>
                <InputDecimal
                  value={form.valorServico}
                  onChange={(value) => setForm({ ...form, valorServico: value ?? undefined })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Valor de Taxas</label>
                <InputDecimal
                  value={form.valorTaxas}
                  onChange={(value) => setForm({ ...form, valorTaxas: value ?? undefined })}
                  prefix="R$ "
                  placeholder="0,00"
                />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Data Solicitação *</label>
                <input
                  type="date"
                  value={form.dataSolicitacao}
                  onChange={(e) => setForm({ ...form, dataSolicitacao: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Previsão Entrega</label>
                <input
                  type="date"
                  value={form.dataPrevistaEntrega}
                  onChange={(e) => setForm({ ...form, dataPrevistaEntrega: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Protocolo</label>
                <input
                  type="text"
                  value={form.protocolo}
                  onChange={(e) => setForm({ ...form, protocolo: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Documentos */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Documentos</label>
              <div className="grid grid-cols-2 gap-2">
                {DOCUMENTOS_PADRAO.map((doc) => {
                  const docForm = form.documentos?.find((d) => d.nome === doc);
                  const isSelected = !!docForm;
                  return (
                    <div key={doc} className="flex items-center gap-2 p-2 border rounded">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleDocumento(doc)}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      <span className={`flex-1 text-sm ${isSelected ? '' : 'text-gray-400'}`}>{doc}</span>
                      {isSelected && (
                        <button
                          type="button"
                          onClick={() => toggleDocumentoRecebido(doc)}
                          className={`p-1 rounded ${docForm?.recebido ? 'text-green-600 bg-green-50' : 'text-gray-400 hover:text-gray-600'}`}
                          title={docForm?.recebido ? 'Recebido' : 'Marcar como recebido'}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
              <textarea
                value={form.observacoes}
                onChange={(e) => setForm({ ...form, observacoes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(emptyForm);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingId ? 'Salvar' : 'Criar'}
              </button>
            </div>
          </form>
        )}

        {/* List */}
        <div className="space-y-2 max-h-[400px] overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
            </div>
          ) : servicos.length === 0 ? (
            <div className="text-center py-8">
              <Truck className="w-12 h-12 text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500">Nenhum serviço de despachante cadastrado</p>
            </div>
          ) : (
            servicos.map((servico) => {
              const docsRecebidos = servico.documentos?.filter((d) => d.recebido).length || 0;
              const docsTotal = servico.documentos?.length || 0;

              return (
                <div
                  key={servico.id}
                  className={`p-4 border rounded-lg ${
                    servico.status === 'CONCLUIDO' ? 'bg-green-50 border-green-200' : 'bg-white'
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 text-xs rounded font-medium ${statusColors[servico.status]}`}>
                          {statusLabels[servico.status]}
                        </span>
                        <span className="font-medium text-gray-900">
                          {TIPOS_SERVICO.find((t) => t.value === servico.tipoServico)?.label}
                        </span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-gray-500">
                        {servico.despachante && <span>Despachante: {servico.despachante}</span>}
                        <span>Solicitado: {formatDate(servico.dataSolicitacao)}</span>
                        {servico.dataPrevistaEntrega && <span>Previsão: {formatDate(servico.dataPrevistaEntrega)}</span>}
                        {servico.valorTotal > 0 && (
                          <span className="font-medium text-gray-700">Total: {formatCurrency(servico.valorTotal)}</span>
                        )}
                        {docsTotal > 0 && (
                          <span className={docsRecebidos === docsTotal ? 'text-green-600' : 'text-yellow-600'}>
                            Docs: {docsRecebidos}/{docsTotal}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleEdit(servico)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingId(servico.id);
                          setShowDeleteDialog(true);
                        }}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title="Excluir"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        type="danger"
        title="Excluir Serviço"
        message="Tem certeza que deseja excluir este serviço de despachante?"
        confirmText="Excluir"
      />
    </Modal>
  );
}
